import {
  CatPrecheckResponse,
  CatRunChangeReason,
  CatRunChangeReasonDetail,
  CatRunChangeResult,
} from "../../../client/types.gen";
import { PerilDataSource } from "../layers/util";

type CatVendorKey = keyof Omit<
  CatPrecheckResponse,
  "warnings" | "blocking_reasons"
>;

// CAT vendor precheck slots whose results feed each peril source. Staleness in
// any of them means premium priced from that source may be out of date.
const STALE_VENDORS_BY_SOURCE = {
  air: ["icm"],
  rms: ["rms"],
  spatialkat: ["spatialkat"],
  rms_22: ["rms_22"],
  rms_22_air_50_50: ["rms_22", "icm", "spatialkat"],
} as const satisfies Record<PerilDataSource, CatVendorKey[]>;

// Single source of user-facing copy for each change trigger. ``limits`` /
// ``deductibles`` / ``layers`` each cover a whole report section, so they
// can't name the specific term that changed.
export const CAT_RUN_CHANGE_REASON_LABELS: Record<CatRunChangeReason, string> =
  {
    addresses: "Addresses",
    effective_date: "Effective date",
    expiration_date: "Expiration date",
    limits: "Limits",
    deductibles: "Deductibles",
    layers: "Layers",
    new_modelable_peril: "Newly modelable peril",
    enrichment: "Enriched data",
    no_snapshot: "No baseline snapshot",
  };

// A reason's label, with its value delta when the backend captured one, e.g.
// "Effective date: 08/31/2026 → 09/01/2026". Deltas are only present for the
// scalar value-diff reasons (the policy dates); all others show the label.
export const reasonDetailLabel = (detail: CatRunChangeReasonDetail): string => {
  const label = CAT_RUN_CHANGE_REASON_LABELS[detail.reason];
  if (detail.before !== null && detail.after !== null) {
    return `${label}: ${detail.before} → ${detail.after}`;
  }
  return label;
};

// Comma-joined reason labels, e.g. "Limits, Deductibles". Empty string when
// there are no reasons.
const reasonsPhrase = (reasons: CatRunChangeReasonDetail[]): string =>
  reasons.map((d) => CAT_RUN_CHANGE_REASON_LABELS[d.reason]).join(", ");

// Glanceable per-vendor freshness for the model tabs: ``fresh`` (green check),
// ``warn`` (terms drifted), ``error`` (needs a full remodel), ``unrun`` (no
// complete run yet — an empty circle rather than a bare tab), ``unverified``
// (results exist but predate the input snapshots, so freshness is unknowable).
export type CatRunStatusSeverity =
  | "fresh"
  | "warn"
  | "error"
  | "unrun"
  | "unverified";
export const catRunStatusSeverity = (
  result: CatRunChangeResult
): CatRunStatusSeverity => {
  // The Modeling tab hides clearance auto-runs: an inactive shadow reads as a
  // vendor that has not been run, never as current, so the underwriter's path is
  // the ordinary Run (fast, since it reprices the warm shadow). Run History is
  // the one place the runs themselves stay visible.
  if (result.is_auto_run_shadow) {
    return "unrun";
  }
  switch (result.change) {
    case "unchanged":
      return "fresh";
    case "terms_changed":
      return "warn";
    case "full_remodel":
      return "error";
    case "no_complete_run":
      return "unrun";
    case "no_baseline":
      return "unverified";
  }
};

// What to tell the underwriter, and how loudly: ``warn`` for results known to
// have drifted, ``info`` for freshness that can't be verified either way.
export type CatRunAdvisory = { severity: "info" | "warn"; summary: string };

// Headline for the per-vendor status banner on the modeling page. The specific
// triggers are listed separately (see ``CAT_RUN_CHANGE_REASON_LABELS``). Null
// when there is nothing to surface.
export const catRunStatusBanner = (
  result: CatRunChangeResult
): CatRunAdvisory | null => {
  // Silent for a shadow, matching the unrun status above.
  if (result.is_auto_run_shadow) {
    return null;
  }
  switch (result.change) {
    case "full_remodel":
      return {
        severity: "warn",
        summary: "CAT results are out of date — re-run CAT modeling.",
      };
    case "terms_changed":
      return {
        severity: "warn",
        summary: "CAT results are stale — re-run CAT modeling.",
      };
    // No call to action: with nothing to diff against the results may well be current,
    // and re-running is not always right (e.g. an account already quoted).
    case "no_baseline":
      return {
        severity: "info",
        summary: "CAT results have unknown freshness.",
      };
    default:
      return null;
  }
};

// Reduce a set of per-vendor change results to one advisory: stale if any vendor
// has drifted. A full re-model outranks a terms-only reprice, so its reasons
// drive the wording. Null when none are stale.
const catResultsAdvisory = (
  results: CatRunChangeResult[]
): CatRunAdvisory | null => {
  const remodel = results.find((r) => r.change === "full_remodel");
  if (remodel) {
    return {
      severity: "warn",
      summary: `CAT results are out of date (${reasonsPhrase(remodel.reasons)} changed since they were generated). Re-run CAT modeling.`,
    };
  }
  const terms = results.find((r) => r.change === "terms_changed");
  if (terms) {
    return {
      severity: "warn",
      summary: `CAT results are stale (${reasonsPhrase(terms.reasons)} changed). Re-run CAT modeling.`,
    };
  }
  // Ranked last, and stated without a call to action: an unverifiable run may well be
  // current, unlike the drifted cases above. It still gets an advisory, since silently
  // pricing off it is what this whole check exists to stop.
  if (results.some((r) => r.change === "no_baseline")) {
    return {
      severity: "info",
      summary: "CAT results have unknown freshness.",
    };
  }
  return null;
};

// Advisory reduced over only the CAT vendors behind the given peril sources.
// Null when none of those vendors needs anything. Unlike the Modeling tab, this
// does not hide clearance auto-run shadows: the Layers tab is about what the
// premium is priced from, where an inactive run is worth knowing about.
export const catAdvisoryForSources = (
  cat: CatPrecheckResponse,
  sources: PerilDataSource[]
): CatRunAdvisory | null => {
  const vendors = [
    ...new Set(sources.flatMap((s) => STALE_VENDORS_BY_SOURCE[s])),
  ];
  return catResultsAdvisory(vendors.map((v) => cat[v].results_change));
};
